import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface RFQItem {
    impaNo?: string;
    description: string;
    partNo?: string;
    altPartNo?: string;
    modelSerial?: string;
    positionNo?: string;
    dimensions?: string;
    requiredQty: number | string;
    uom?: string;
    quality?: string;
    remark?: string;
    deliveryPriority?: string;
    expectedDeliveryDate?: string;
    hsCode?: string;
}

export interface RFQData {
    // Header
    rfqNumber: string;
    revisionNo?: string;
    createdAt: string;
    dueDate?: string;
    quoteDeadline?: string;
    quoteValidityDays?: string;
    urgencyLevel?: string;
    requisitionRef?: string;
    currency?: string;
    rfqStatus?: string;
    department?: string;
    estimatedTotalCost?: string;

    // Customer / Org
    customerName: string;

    // Vessel
    vesselName: string;
    imoNumber?: string;
    hullNumber?: string;
    serialNumber?: string;

    // Equipment
    brand?: string;
    model?: string;
    category?: string;
    subCategory?: string;
    equipmentTags?: string;

    // Delivery
    deliveryRequiredAt?: string;
    deliveryType?: string;

    // Port Delivery
    supplyPort?: string;
    portCountry?: string;
    portEtaDate?: string;
    portAgentName?: string;
    portAgentPhone?: string;
    portAgentEmail?: string;
    portAddress?: string;

    // Vessel Delivery
    vesselCurrentLocation?: string;
    vesselNextPort?: string;
    vesselEtaWindow?: string;
    vesselInstructions?: string;

    // Logistics
    leadDate?: string;
    incoterms?: string;
    logisticContainer?: string;
    partialQuoteAllowed?: string;
    splitOrderAllowed?: string;

    // Contact Person
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    contactDepartment?: string;

    // Terms & Conditions
    requiredPaymentTerms?: string;
    requiredCertificates?: string[];
    requiredDocuments?: string[];
    packingInstructions?: string;
    partialDeliveryAllowed?: string;

    // Remarks
    remarks?: string;

    // Items
    items: RFQItem[];

    // Letterhead branding
    companyLogoUrl?: string | null;
    companyNameForLetterhead?: string;
}

const BRAND_COLOR: [number, number, number] = [10, 36, 99];   // Dark navy
const ACCENT_COLOR: [number, number, number] = [0, 112, 192]; // Blue accent
const LIGHT_BG: [number, number, number] = [240, 245, 255];   // Very light blue
const TEXT_DARK: [number, number, number] = [30, 30, 30];
const TEXT_MUTED: [number, number, number] = [100, 100, 110];
const DIVIDER: [number, number, number] = [200, 210, 230];

function sectionHeader(doc: jsPDF, title: string, y: number, pageWidth: number): number {
    doc.setFillColor(...BRAND_COLOR);
    doc.rect(14, y, pageWidth - 28, 7, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(title.toUpperCase(), 17, y + 5);
    doc.setTextColor(...TEXT_DARK);
    return y + 11;
}

function labelValue(doc: jsPDF, label: string, value: string, x: number, y: number, labelWidth = 45): void {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...TEXT_MUTED);
    doc.text(label + ':', x, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_DARK);
    doc.text(value || '-', x + labelWidth, y);
}

function addHorizontalRule(doc: jsPDF, y: number, pageWidth: number): void {
    doc.setDrawColor(...DIVIDER);
    doc.setLineWidth(0.3);
    doc.line(14, y, pageWidth - 14, y);
}

function fmtDate(val?: string): string {
    if (!val || val === '-') return '-';
    try {
        return new Date(val).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric'
        });
    } catch {
        return val;
    }
}

export const downloadRFQPDF = async (data: RFQData) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let y = 14;

    // ─── TITLE BANNER / LETTERHEAD ───────────────────────────────────────────
    const { drawDocumentLetterhead } = await import('./document-letterhead');
    y = await drawDocumentLetterhead(doc, {
        docTitle: 'REQUEST FOR QUOTATION',
        companyName: data.companyNameForLetterhead || data.customerName || 'Ship Management Company',
        logoUrl: data.companyLogoUrl,
        accentRgb: BRAND_COLOR,
    });

    // Right side metadata under letterhead
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...TEXT_DARK);
    doc.text(`RFQ No: ${data.rfqNumber}`, pageWidth - 14, y, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Date: ${fmtDate(data.createdAt)}`, pageWidth - 14, y + 5, { align: 'right' });
    if (data.revisionNo) {
        doc.text(`Rev: ${data.revisionNo}`, pageWidth - 14, y + 10, { align: 'right' });
    }
    if (data.rfqStatus) {
        doc.text(`Status: ${data.rfqStatus}`, pageWidth - 14, y + 15, { align: 'right' });
    }

    y += 20;

    // ─── QUICK SUMMARY BAR ───────────────────────────────────────────────────
    doc.setFillColor(...LIGHT_BG);
    doc.rect(14, y, pageWidth - 28, 16, 'F');
    doc.setDrawColor(...ACCENT_COLOR);
    doc.setLineWidth(0.5);
    doc.rect(14, y, pageWidth - 28, 16, 'S');

    const col = (pageWidth - 28) / 4;
    const summaryItems = [
        { label: 'Quote Deadline', value: fmtDate(data.quoteDeadline || data.dueDate) },
        { label: 'Lead Date', value: fmtDate(data.leadDate) },
        { label: 'Urgency', value: data.urgencyLevel || '-' },
        { label: 'Currency', value: data.currency || '-' },
    ];
    summaryItems.forEach((item, i) => {
        const sx = 17 + i * col;
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...TEXT_MUTED);
        doc.text(item.label, sx, y + 6);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...BRAND_COLOR);
        doc.text(item.value, sx, y + 13);
    });
    doc.setTextColor(...TEXT_DARK);
    y += 20;

    // ─── SECTION 1: RFQ HEADER INFO ─────────────────────────────────────────
    y = sectionHeader(doc, '1. RFQ Header Information', y, pageWidth);
    const half = (pageWidth - 28) / 2;
    labelValue(doc, 'RFQ Number', data.rfqNumber, 14, y);
    labelValue(doc, 'Revision No', data.revisionNo || '00', 14 + half, y);
    y += 6;
    labelValue(doc, 'Created Date', fmtDate(data.createdAt), 14, y);
    labelValue(doc, 'Quote Deadline', fmtDate(data.quoteDeadline || data.dueDate), 14 + half, y);
    y += 6;
    labelValue(doc, 'Quote Validity', data.quoteValidityDays ? `${data.quoteValidityDays} days` : '-', 14, y);
    labelValue(doc, 'Currency', data.currency || '-', 14 + half, y);
    y += 6;
    labelValue(doc, 'Urgency Level', data.urgencyLevel || '-', 14, y);
    labelValue(doc, 'Requisition Ref', data.requisitionRef || '-', 14 + half, y);
    y += 6;
    labelValue(doc, 'Department', data.department?.toUpperCase() || '-', 14, y);
    labelValue(doc, 'Estimated Total', data.estimatedTotalCost ? `${data.currency} ${data.estimatedTotalCost}` : '-', 14 + half, y);
    y += 8;
    addHorizontalRule(doc, y, pageWidth);
    y += 4;

    // ─── SECTION 2: CUSTOMER INFORMATION ────────────────────────────────────
    y = sectionHeader(doc, '2. Customer Information', y, pageWidth);
    labelValue(doc, 'Customer', data.customerName, 14, y);
    y += 6;
    if (data.contactName || data.contactEmail || data.contactPhone) {
        labelValue(doc, 'Contact Person', data.contactName || '-', 14, y);
        labelValue(doc, 'Department', data.contactDepartment || '-', 14 + half, y);
        y += 6;
        labelValue(doc, 'Email', data.contactEmail || '-', 14, y);
        labelValue(doc, 'Phone', data.contactPhone || '-', 14 + half, y);
        y += 6;
    }
    addHorizontalRule(doc, y, pageWidth);
    y += 4;

    // ─── SECTION 3: VESSEL INFORMATION ──────────────────────────────────────
    y = sectionHeader(doc, '3. Vessel Information', y, pageWidth);
    labelValue(doc, 'Vessel Name', data.vesselName || '-', 14, y);
    labelValue(doc, 'IMO Number', data.imoNumber || '-', 14 + half, y);
    y += 6;
    labelValue(doc, 'Hull No', data.hullNumber || '-', 14, y);
    labelValue(doc, 'Serial No', data.serialNumber || '-', 14 + half, y);
    y += 8;
    addHorizontalRule(doc, y, pageWidth);
    y += 4;

    // ─── SECTION 4: EQUIPMENT INFORMATION ───────────────────────────────────
    y = sectionHeader(doc, '4. Equipment Information', y, pageWidth);
    labelValue(doc, 'Brand', data.brand || '-', 14, y);
    labelValue(doc, 'Model', data.model || '-', 14 + half, y);
    y += 6;
    labelValue(doc, 'Category', data.category || '-', 14, y);
    labelValue(doc, 'Sub Category', data.subCategory || '-', 14 + half, y);
    y += 6;
    if (data.equipmentTags) {
        labelValue(doc, 'Equipment Tags', data.equipmentTags, 14, y);
        y += 6;
    }
    addHorizontalRule(doc, y, pageWidth);
    y += 4;

    // ─── SECTION 5: DELIVERY INFORMATION ────────────────────────────────────
    // Check if page break is needed
    if (y > pageHeight - 80) {
        doc.addPage();
        y = 14;
    }
    y = sectionHeader(doc, '5. Delivery Information', y, pageWidth);

    const deliveryAt = data.deliveryRequiredAt || 'port';
    labelValue(doc, 'Delivery At', deliveryAt === 'port' ? 'Port Delivery' : 'Vessel Delivery', 14, y);
    if (data.deliveryType) {
        labelValue(doc, 'Delivery Type', data.deliveryType, 14 + half, y);
    }
    y += 6;

    if (deliveryAt === 'port') {
        labelValue(doc, 'Supply Port', data.supplyPort || '-', 14, y);
        labelValue(doc, 'Port Country', data.portCountry || '-', 14 + half, y);
        y += 6;
        labelValue(doc, 'Port ETA Date', fmtDate(data.portEtaDate), 14, y);
        labelValue(doc, 'Port Agent', data.portAgentName || '-', 14 + half, y);
        y += 6;
        if (data.portAgentEmail || data.portAgentPhone) {
            labelValue(doc, 'Agent Email', data.portAgentEmail || '-', 14, y);
            labelValue(doc, 'Agent Phone', data.portAgentPhone || '-', 14 + half, y);
            y += 6;
        }
        if (data.portAddress) {
            labelValue(doc, 'Port Address', data.portAddress, 14, y);
            y += 6;
        }
    } else {
        labelValue(doc, 'Current Location', data.vesselCurrentLocation || '-', 14, y);
        labelValue(doc, 'Next Port', data.vesselNextPort || '-', 14 + half, y);
        y += 6;
        labelValue(doc, 'ETA Window', data.vesselEtaWindow || '-', 14, y);
        y += 6;
        if (data.vesselInstructions) {
            labelValue(doc, 'Instructions', data.vesselInstructions, 14, y);
            y += 6;
        }
    }
    addHorizontalRule(doc, y, pageWidth);
    y += 4;

    // ─── SECTION 6: LOGISTICS & PROCUREMENT ─────────────────────────────────
    y = sectionHeader(doc, '6. Logistics & Procurement', y, pageWidth);
    labelValue(doc, 'Incoterms', data.incoterms || '-', 14, y);
    labelValue(doc, 'Logistic Container', data.logisticContainer || '-', 14 + half, y);
    y += 6;
    labelValue(doc, 'Partial Quote', data.partialQuoteAllowed || '-', 14, y);
    labelValue(doc, 'Split Order', data.splitOrderAllowed || '-', 14 + half, y);
    y += 8;
    addHorizontalRule(doc, y, pageWidth);
    y += 4;

    // ─── SECTION 7: TERMS & CONDITIONS ──────────────────────────────────────
    if (y > pageHeight - 80) {
        doc.addPage();
        y = 14;
    }
    y = sectionHeader(doc, '7. Terms & Conditions', y, pageWidth);
    labelValue(doc, 'Payment Terms', data.requiredPaymentTerms || '-', 14, y);
    labelValue(doc, 'Partial Delivery', data.partialDeliveryAllowed || '-', 14 + half, y);
    y += 6;

    if (data.requiredCertificates && data.requiredCertificates.length > 0) {
        labelValue(doc, 'Certificates', data.requiredCertificates.join(', '), 14, y);
        y += 6;
    }
    if (data.requiredDocuments && data.requiredDocuments.length > 0) {
        labelValue(doc, 'Documents', data.requiredDocuments.join(', '), 14, y);
        y += 6;
    }
    if (data.packingInstructions) {
        labelValue(doc, 'Packing Instr.', data.packingInstructions, 14, y);
        y += 6;
    }
    addHorizontalRule(doc, y, pageWidth);
    y += 4;

    // ─── SECTION 8: ITEMS TABLE ──────────────────────────────────────────────
    if (y > pageHeight - 60) {
        doc.addPage();
        y = 14;
    }

    doc.setFillColor(...BRAND_COLOR);
    doc.rect(14, y, pageWidth - 28, 7, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('8. ITEM LIST', 17, y + 5);
    doc.setTextColor(...TEXT_DARK);
    y += 9;

    const tableHeaders = [['#', 'IMPA', 'Description / Model', 'Part No', 'Alt Part', 'Serial', 'Dim.', 'Qty', 'UoM', 'Quality', 'Prio', 'Remarks']];
    const tableData = data.items.map((item, index) => {
        const desc = item.description || '-';
        const modelInfo = item.modelSerial ? `\n(M/S: ${item.modelSerial})` : '';
        return [
            item.positionNo || (index + 1).toString(),
            item.impaNo || '-',
            desc + modelInfo,
            item.partNo || '-',
            item.altPartNo || '-',
            item.modelSerial || '-',
            item.dimensions || '-',
            String(item.requiredQty || '0'),
            item.uom || '-',
            item.quality || '-',
            item.deliveryPriority || '-',
            item.remark || '-',
        ];
    });

    autoTable(doc, {
        startY: y,
        head: tableHeaders,
        body: tableData,
        theme: 'grid',
        headStyles: {
            fillColor: ACCENT_COLOR,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 7,
            cellPadding: 2,
        },
        bodyStyles: {
            fontSize: 7,
            cellPadding: 2,
            textColor: TEXT_DARK,
        },
        alternateRowStyles: { fillColor: [247, 250, 255] },
        margin: { left: 14, right: 14 },
        columnStyles: {
            0: { cellWidth: 7 },
            1: { cellWidth: 14 },
            2: { cellWidth: 'auto' },
            3: { cellWidth: 18 },
            4: { cellWidth: 18 },
            5: { cellWidth: 14 },
            6: { cellWidth: 12 },
            7: { cellWidth: 8 },
            8: { cellWidth: 8 },
            9: { cellWidth: 10 },
            10: { cellWidth: 15 },
            11: { cellWidth: 20 },
        },
        didDrawPage: (hookData) => {
            // Repeat section header on new pages
            if (hookData.pageNumber > 1) {
                doc.setFillColor(...BRAND_COLOR);
                doc.rect(14, 10, pageWidth - 28, 7, 'F');
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(255, 255, 255);
                doc.text(`RFQ ${data.rfqNumber} - ITEM LIST (continued)`, 17, 15);
                doc.setTextColor(...TEXT_DARK);
            }
        },
    });

    // ─── SECTION 9: REMARKS ─────────────────────────────────────────────────
    if (data.remarks) {
        const finalY = (doc as any).lastAutoTable.finalY + 6;
        let ry = finalY;
        if (ry > pageHeight - 40) {
            doc.addPage();
            ry = 14;
        }
        ry = sectionHeader(doc, '9. Remarks / Special Instructions', ry, pageWidth);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...TEXT_DARK);
        const splitRemarks = doc.splitTextToSize(data.remarks, pageWidth - 32);
        doc.text(splitRemarks, 17, ry);
    }

    // ─── FOOTER ──────────────────────────────────────────────────────────────
    const { drawPlatformPdfFooter } = await import('./document-letterhead');
    drawPlatformPdfFooter(doc, { center: `RFQ: ${data.rfqNumber}` });

    const sanitizeFilePart = (value?: string) =>
        (value || '')
            .trim()
            .replace(/[^\w.-]+/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '');
    const imoPart = sanitizeFilePart(data.imoNumber) || 'NOIMO';
    const tagsPart = sanitizeFilePart((data.equipmentTags || '').replace(/,/g, '-')) || 'NOTAGS';
    doc.save(`${imoPart}_${tagsPart}_RFQ_${data.rfqNumber}.pdf`);
};
